import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';

import type { AuthMeResponse } from '@/api/auth.api';
import {
  listCredentialPolicies,
  listRotationEvents,
  rotateCredential,
  SHA256_FINGERPRINT_RE,
  type CredentialPolicyStatus,
  type CredentialPolicyView,
  type CsRotationOutcome,
} from '@/api/cs/operations.api';
import { CsErrorCard, CsInlineError, CsLoading, SELECT_CLS } from '@/components/cs/PageStates';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { hasCapability } from '@/lib/capabilities';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Credentials (spec §3.10.3). Rotation-policy table with
 * status chips + a rotate dialog (outcome, optional sha256 fingerprint
 * — validated client-side BEFORE the server sees it, never the secret
 * itself — and a reason) + rotation-event history. Writes need
 * manage_cs_credentials (client-side visibility only).
 */

const STATUS_CHIP: Record<CredentialPolicyStatus, { label: string; variant: BadgeProps['variant'] }> = {
  ok: { label: 'OK', variant: 'success' },
  due_soon: { label: 'Due soon', variant: 'warning' },
  overdue: { label: 'Overdue', variant: 'danger' },
  never_rotated: { label: 'Never rotated', variant: 'neutral' },
};

export function CredentialsTab({ me }: { me: AuthMeResponse }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canManage = hasCapability(me, 'manage_cs_credentials');

  const policiesQuery = useQuery({
    queryKey: ['cs', 'credentials', 'policies'],
    queryFn: listCredentialPolicies,
  });
  const eventsQuery = useQuery({
    queryKey: ['cs', 'credentials', 'events'],
    queryFn: () => listRotationEvents(undefined, 50),
  });

  const [rotating, setRotating] = useState<CredentialPolicyView | null>(null);

  if (policiesQuery.isLoading) return <CsLoading />;
  if (policiesQuery.error) {
    return (
      <CsErrorCard
        title="Couldn't load credential policies"
        error={policiesQuery.error}
        fallback="Unknown error"
      />
    );
  }

  const policies = policiesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-kw-muted">
        Rotation tracking only — secrets never live here. Env-backed credentials (not managed by the hub) are
        rotated at the provider, then marked rotated.
      </p>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="px-4 py-2.5 font-medium">Provider</th>
              <th className="px-4 py-2.5 font-medium">Policy</th>
              <th className="px-4 py-2.5 font-medium">Last rotated</th>
              <th className="px-4 py-2.5 font-medium">Due</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              {canManage ? <th className="px-4 py-2.5 font-medium" /> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {policies.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="px-4 py-8 text-center text-[13px] text-kw-muted">
                  No credential policies yet — the seed creates them.
                </td>
              </tr>
            ) : (
              policies.map((p) => {
                const chip = STATUS_CHIP[p.status];
                return (
                  <tr key={p.id} className="text-[13px] text-kw-shark">
                    <td className="px-4 py-2.5 font-medium">
                      {p.provider}
                      {!p.managedByHub ? (
                        <span className="ml-2 text-[11px] text-kw-muted">(env-backed)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-kw-muted">every {p.rotationPolicyDays}d</td>
                    <td className="px-4 py-2.5 text-kw-muted">
                      {p.lastRotatedAt ? new Date(p.lastRotatedAt).toLocaleDateString() : 'never'}
                    </td>
                    <td className="px-4 py-2.5 text-kw-muted">
                      {p.dueAt ? new Date(p.dueAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={chip.variant}>{chip.label}</Badge>
                    </td>
                    {canManage ? (
                      <td className="px-4 py-2.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRotating(p);
                          }}
                        >
                          <KeyRound className="size-4" />
                          Rotate
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>

      <section>
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Rotation history</h2>
        <Card className="mt-2 p-0">
          {(eventsQuery.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-kw-muted">No rotation events yet.</p>
          ) : (
            <ul className="divide-y divide-kw-border">
              {(eventsQuery.data ?? []).map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-[13px]">
                  <span className="font-medium text-kw-shark">{e.provider}</span>
                  <Badge
                    variant={
                      e.outcome === 'rotated' ? 'success' : e.outcome === 'test_failed' ? 'danger' : 'info'
                    }
                  >
                    {e.outcome}
                  </Badge>
                  {e.toFingerprint ? (
                    <span className="font-mono text-[11px] text-kw-muted">…{e.toFingerprint}</span>
                  ) : null}
                  {e.error ? <span className="text-[12px] text-kw-danger-text">{e.error}</span> : null}
                  <span className="ml-auto text-[12px] text-kw-muted">
                    {new Date(e.rotatedAt).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {rotating ? (
        <RotateModal
          policy={rotating}
          onClose={() => {
            setRotating(null);
          }}
          onRotated={() => {
            setRotating(null);
            void queryClient.invalidateQueries({ queryKey: ['cs', 'credentials'] });
            toast.show('Rotation recorded.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Rotate dialog
// ─────────────────────────────────────────────────────────────────────────

function RotateModal({
  policy,
  onClose,
  onRotated,
}: {
  policy: CredentialPolicyView;
  onClose: () => void;
  onRotated: () => void;
}) {
  const [outcome, setOutcome] = useState<CsRotationOutcome>(
    policy.managedByHub ? 'rotated' : 'marked_manual',
  );
  const [fingerprint, setFingerprint] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      rotateCredential(policy.id, {
        outcome,
        ...(fingerprint.trim() !== '' ? { fingerprint: fingerprint.trim() } : {}),
        ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
      }),
    onSuccess: onRotated,
  });

  const fingerprintInvalid = fingerprint.trim() !== '' && !SHA256_FINGERPRINT_RE.test(fingerprint.trim());

  return (
    <Modal open onClose={onClose} title={`Rotate credential: ${policy.provider}`} size="sm">
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (fingerprintInvalid) return;
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          {policy.managedByHub
            ? 'Rotate the secret at the provider / integration first, then record the outcome here.'
            : 'This credential is env-backed — rotate it at the provider, then mark it rotated.'}
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Outcome</span>
          <select
            aria-label="Rotation outcome"
            value={outcome}
            onChange={(e) => {
              setOutcome(e.target.value as CsRotationOutcome);
            }}
            className={SELECT_CLS}
          >
            <option value="rotated">Rotated</option>
            <option value="marked_manual">Marked rotated (manual / env-backed)</option>
            <option value="test_failed">Test failed</option>
          </select>
        </label>
        <Input
          label="New-secret fingerprint (optional)"
          name="cs-rotate-fingerprint"
          autoComplete="off"
          spellCheck={false}
          value={fingerprint}
          onChange={(e) => {
            setFingerprint(e.target.value);
          }}
          error={
            fingerprintInvalid
              ? 'Must be a 64-character lowercase sha256 hex digest — never the secret itself.'
              : undefined
          }
          hint="sha256 of the NEW secret, e.g. `echo -n $SECRET | shasum -a 256`. Only the first 12 chars are stored."
        />
        <Input
          label="Reason (optional, audited)"
          name="cs-rotate-reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
        <CsInlineError error={mutation.error} fallback="Could not record the rotation." />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={fingerprintInvalid}>
            Record rotation
          </Button>
        </div>
      </form>
    </Modal>
  );
}
